import React, { useMemo, useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import TextField from '../fields/TextField';
import './HeroHomeEditor.css';
import './ArticleSubmitEditor.css';

function deepClone(o) {
  try {
    return JSON.parse(JSON.stringify(o || {}));
  } catch {
    return {};
  }
}

function mergeContent(comp, changes) {
  const raw =
    typeof comp.content === 'string'
      ? JSON.parse(comp.content || '{}')
      : deepClone(comp.content);
  const ch = changes[comp.id] || {};
  Object.entries(ch).forEach(([pathStr, val]) => {
    if (pathStr === '__mediaId__') return;
    const parts = pathStr.split('.');
    let cur = raw;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      const next = parts[i + 1];
      if (cur[p] == null) cur[p] = /^\d+$/.test(String(next)) ? [] : {};
      cur = cur[p];
    }
    cur[parts[parts.length - 1]] = val;
  });
  return raw;
}

const SECTIONS = [
  { key: 'progress', label: 'Индикатор шагов (progress)' },
  { key: 'buttons', label: 'Кнопки (buttons)' },
  { key: 'errors', label: 'Сообщения об ошибках (errors)' },
  { key: 'step1', label: 'Шаг 1 — данные статьи (step1)' },
  { key: 'step2', label: 'Шаг 2 — авторы (step2)' },
  { key: 'step3', label: 'Шаг 3 — файлы (step3)' },
  { key: 'step4', label: 'Шаг 4 — завершение (step4)' },
];

function renderNested(compId, lang, pathParts, obj, onUpdate) {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object' || Array.isArray(obj)) return null;

  return Object.entries(obj).map(([k, v]) => {
    const nextPath = [...pathParts, k];
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      return (
        <div key={nextPath.join('.')} className="article-submit-nested">
          <div className="article-submit-nested__title">{k}</div>
          {renderNested(compId, lang, nextPath, v, onUpdate)}
        </div>
      );
    }
    return (
      <div key={nextPath.join('.')} className="article-submit-row">
        <label className="article-submit-row__key">
          <code>{k}</code>
        </label>
        <TextField
          value={String(v ?? '')}
          onChange={(nv) => onUpdate(compId, [lang, ...nextPath], nv)}
          isEmpty={!String(v ?? '').trim()}
          hasTranslation
        />
      </div>
    );
  });
}

export default function ArticleSubmitEditor({ component: comp, languages, changes, onUpdate }) {
  const codes = (languages || []).map((l) => l.code);
  const langs = codes.length ? codes : ['en', 'ru', 'uz'];
  const [lang, setLang] = useState(langs[0] || 'en');

  useEffect(() => {
    if (!langs.length) return;
    if (!langs.includes(lang)) setLang(langs[0]);
  }, [langs, lang]);

  const content = useMemo(() => mergeContent(comp, changes), [comp, changes]);
  const root = content[lang] || {};

  return (
    <div className="hero-home-editor article-submit-editor">
      <div className="hero-home-editor__head">
        <Send size={22} />
        <div>
          <h3 className="hero-home-editor__title">Отправка статьи (форма)</h3>
          <p className="hero-home-editor__hint">
            Тексты интерфейса многошаговой формы <code>/article-submit</code>. Редактируйте по одному языку за
            раз — полей много, структура совпадает с блоками формы на сайте.
          </p>
        </div>
      </div>

      <div className="article-submit-lang-bar">
        {langs.map((code) => (
          <button
            key={code}
            type="button"
            className={`article-submit-lang-btn ${lang === code ? 'active' : ''}`}
            onClick={() => setLang(code)}
          >
            {code.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="article-submit-sections">
        {SECTIONS.map(({ key, label }) => (
          <details key={key} className="article-submit-details" open>
            <summary>{label}</summary>
            <div className="article-submit-details__body">
              {renderNested(comp.id, lang, [key], root[key] ?? {}, onUpdate)}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
